import type { Plan } from "./api/client.ts";

/**
 * Display names for a plan, matching the tier names on the marketing site's
 * pricing page.
 */
const PLAN_LABELS: Record<Plan, string> = {
  free: "Free",
  day: "Day pass",
  pro: "Pro",
};

export function planLabel(plan: Plan | undefined): string {
  return PLAN_LABELS[plan ?? "free"];
}

/**
 * Days remaining until `iso`, matching the server's own `$expiry <= $now`
 * boundary in `Plan.php`. Zero or negative means expired.
 */
export function daysRemaining(iso: string): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / msPerDay);
}

/**
 * Describe when a day pass or Pro subscription runs out, in days-remaining
 * terms matching the server's own `$expiry <= $now` boundary in `Plan.php`.
 */
export function formatExpiry(iso: string): string {
  const expiry = new Date(iso);
  const date = expiry.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  const daysLeft = daysRemaining(iso);
  if (daysLeft <= 0) return `Expired ${date}`;
  if (daysLeft === 1) return `Expires ${date} · 1 day left`;
  return `Expires ${date} · ${daysLeft} days left`;
}

/**
 * Just the expiry date, with no days-left count — the account dropdown pairs
 * this with a progress bar instead of the count `formatExpiry()` appends for
 * the paywall's prose.
 */
export function formatExpiryDate(iso: string): string {
  const expiry = new Date(iso);
  const date = expiry.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  return expiry.getTime() <= Date.now() ? `Expired ${date}` : `Expires ${date}`;
}

/**
 * How far through the current plan period `now` is, as 0–1.
 *
 * Null when the period's start isn't known — an account whose plan predates
 * `plan_started_at` being recorded, or one that has never held a paid plan —
 * so the caller can hide the bar rather than draw one against a guessed
 * start.
 */
export function planProgress(startedAtIso: string | null | undefined, expiresAtIso: string): number | null {
  if (startedAtIso == null) return null;

  const start = new Date(startedAtIso).getTime();
  const end = new Date(expiresAtIso).getTime();
  if (end <= start) return null;

  return Math.min(1, Math.max(0, (Date.now() - start) / (end - start)));
}
